/* ============================================
   Native Possum — np-utilities.js
   Shared globals: npFormatMoney, npCeilToFive, npEscapeHtml,
   npFilterHooks, npSwitchCurrency.
   Load order: sync (no defer) in theme.liquid, after np-currency-map
   (window.NP_CURRENCY) and before np-header.js / other NP scripts.
   npSwitchCurrency: form.submit() is the only thing that changes the
   market server-side — never remove it.
   ============================================ */

(function() {
  'use strict';

  // ============================================
  // CURRENCY FALLBACK
  // Used only if window.NP_CURRENCY is missing — i.e. the
  // np-currency-map render in theme.liquid didn't run. Keep in
  // sync with np-currency-map.liquid, but that snippet is the
  // source of truth: add new currencies THERE, not here.
  // ============================================
  var NP_CURRENCY_FALLBACK = {
    'NZD': { symbol: '$', prefix: ''    },
    'AUD': { symbol: '$', prefix: 'AUD' },
    'USD': { symbol: '$', prefix: 'USD' },
    'CAD': { symbol: '$', prefix: 'CAD' },
    'GBP': { symbol: '£', prefix: ''    },
    'EUR': { symbol: '€', prefix: ''    }
  };

  // Thousands separators, matching Liquid's money filter output.
  function npGroupThousands(str) {
    var parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  // ============================================
  // CURRENCY FORMATTING
  // Returns prefix + symbol + amount, e.g. CAD$299 or $298.80.
  // Matches the Liquid money_without_trailing_zeros output.
  // Used by: np-header.js, header-scripts.liquid
  // ============================================
  window.npFormatMoney = function(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents.replace(/[^0-9]/g, ''), 10);
    }
    if (isNaN(cents)) cents = 0;

    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
      ? window.Shopify.currency.active
      : 'NZD';

    var map = window.NP_CURRENCY || NP_CURRENCY_FALLBACK;
    var entry = map[currency] || { symbol: '$', prefix: '' };

    var amount = cents / 100;
    var formatted = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);

    return entry.prefix + entry.symbol + npGroupThousands(formatted);
  };

  // ============================================
  // ROUND UP TO THE NEXT 5 (in cents)
  // For "$X away from free shipping" messages. Always rounds UP so
  // the customer is never told they are closer than they are.
  // 1150 -> 1500, 1500 -> 1500, 1 -> 500.
  // Used by: np-bag-drawer.liquid, main-cart.liquid
  // ============================================
  window.npCeilToFive = function(cents) {
    var dollars = Math.ceil(cents / 100);
    return Math.max(5, Math.ceil(dollars / 5) * 5) * 100;
  };

  // ============================================
  // COLLAPSIBLE PANELS (disclosure)
  // Any trigger with data-np-disclosure + aria-controls keeps its panel
  // `inert` whenever aria-expanded isn't "true". Collapsed content (hidden by
  // max-height for the animation) is then skipped by Tab and screen readers.
  // Only for click-to-open panels — never for hover menus (inert blocks clicks).
  // Used by: main-product (size/care guides), collection (mobile filter
  //          accordions), np-mobile-menu (menu accordions)
  // ============================================
  function npSyncDisclosure(trigger) {
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (panel) panel.inert = trigger.getAttribute('aria-expanded') !== 'true';
  }
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-np-disclosure][aria-controls]').forEach(function(trigger) {
      npSyncDisclosure(trigger);
      new MutationObserver(function() { npSyncDisclosure(trigger); })
        .observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });
    });
  });

  // ============================================
  // LAYERS (drawers, sheets, lightboxes, popups)
  // One helper so every popup behaves the same for keyboard and
  // screen-reader users. Visuals, animation and scroll-lock stay
  // with each component — this only handles:
  //   - everything outside the layer (and its `keep` areas) goes inert
  //   - focus moves in on open and back to the trigger on close
  //   - ONE Escape handler: closes the top layer only
  //   - aria-expanded on the trigger
  //   - the panel itself is `inert` while closed (so an off-screen drawer
  //     can't be tabbed into) — give closed panels the `inert` attribute
  //     in their markup so this holds before any JS runs
  // Layers stack (e.g. currency drawer over the mobile menu).
  //
  //   npLayer.open(panel, {
  //     trigger,            // element that opened it (focus returns here)
  //     keep: [els],        // areas that stay usable (e.g. header for the bag)
  //     closeOnKeepUse,     // true: using anything in `keep` (other than
  //                         //   the trigger) asks the layer to close
  //     initialFocus,       // element or selector; default first focusable
  //     onRequestClose      // REQUIRED: the component's own close()
  //   });
  //   npLayer.close(panel);   // call from the component's close()
  //   npLayer.isOpen(panel);
  // ============================================
  (function() {
    var stack = [];
    var applied = [];           // elements this helper made inert
    var SKIP = { SCRIPT: 1, STYLE: 1, LINK: 1, TEMPLATE: 1, NOSCRIPT: 1 };
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
      'select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

    function release() {
      applied.forEach(function(el) { el.inert = false; });
      applied = [];
    }

    function applyTop() {
      release();
      var top = stack[stack.length - 1];
      if (!top) return;
      // Walk down from <body>: anything that is (or is inside) a kept element
      // stays live; ancestors of kept elements are walked into; everything
      // else is made inert. Kept elements' own contents are never touched.
      var keep = new Set([top.panel].concat(top.keep));
      var ancestors = new Set();
      keep.forEach(function(el) {
        for (var n = el.parentElement; n && n !== document.documentElement; n = n.parentElement) ancestors.add(n);
      });
      (function walk(node) {
        Array.prototype.forEach.call(node.children, function(child) {
          if (keep.has(child) || SKIP[child.tagName]) return;
          if (ancestors.has(child)) { walk(child); return; }
          if (child.inert) return;
          child.inert = true;
          applied.push(child);
        });
      })(document.body);
    }

    function focusInto(layer) {
      var target = layer.initialFocus;
      if (typeof target === 'string') target = layer.panel.querySelector(target);
      if (!target) {
        target = Array.prototype.find.call(layer.panel.querySelectorAll(FOCUSABLE), function(el) {
          return el.getClientRects().length > 0;
        });
      }
      if (!target) {
        if (!layer.panel.hasAttribute('tabindex')) layer.panel.setAttribute('tabindex', '-1');
        target = layer.panel;
      }
      target.focus({ preventScroll: true });
    }

    function onKeepUse(e) {
      var top = stack[stack.length - 1];
      if (!top || !top.closeOnKeepUse) return;
      if (top.panel.contains(e.target)) return;
      if (top.trigger && top.trigger.contains(e.target)) return;
      if (top.keep.some(function(k) { return k.contains(e.target); })) top.onRequestClose();
    }

    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Escape' || !stack.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      stack[stack.length - 1].onRequestClose();
    }, true);
    document.addEventListener('pointerdown', onKeepUse, true);
    document.addEventListener('focusin', onKeepUse, true);

    window.npLayer = {
      open: function(panel, opts) {
        if (!panel) return;
        opts = opts || {};
        if (this.isOpen(panel)) return;
        var layer = {
          panel: panel,
          trigger: opts.trigger || (document.activeElement !== document.body ? document.activeElement : null),
          keep: (opts.keep || []).filter(Boolean),
          closeOnKeepUse: !!opts.closeOnKeepUse,
          initialFocus: opts.initialFocus,
          onRequestClose: opts.onRequestClose || function() { window.npLayer.close(panel); }
        };
        panel.inert = false;
        stack.push(layer);
        if (layer.trigger && layer.trigger.hasAttribute('aria-expanded')) layer.trigger.setAttribute('aria-expanded', 'true');
        applyTop();
        // Wait a frame so the component's own "show" styles have applied.
        requestAnimationFrame(function() { focusInto(layer); });
      },
      close: function(panel) {
        var i = -1;
        for (var k = stack.length - 1; k >= 0; k--) { if (stack[k].panel === panel) { i = k; break; } }
        if (i === -1) return;
        var layer = stack.splice(i, 1)[0];
        panel.inert = true;
        applyTop();
        var t = layer.trigger;
        if (t && t.hasAttribute('aria-expanded')) t.setAttribute('aria-expanded', 'false');
        var active = document.activeElement;
        var focusWasInside = !active || active === document.body || panel.contains(active);
        if (focusWasInside && t && document.contains(t) && t.getClientRects().length) {
          t.focus({ preventScroll: true });
        }
      },
      isOpen: function(panel) {
        return stack.some(function(l) { return l.panel === panel; });
      }
    };
  })();

  // ============================================
  // HTML ESCAPING
  // Prevents XSS in dynamically inserted content.
  // Used by: np-header.js, header-scripts.liquid
  // ============================================
  window.npEscapeHtml = function(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // ============================================
  // FILTER HOOK REGISTRY
  //
  // Replaces the fragile setTimeout hooking chain where
  // np-collection-mobile.js (100ms) and np-infinite-scroll.js (300ms)
  // wrapped npApplyFilters at different timings.
  //
  // Now each file registers callbacks in guaranteed order.
  //
  // beforeApply hooks CAN return a Promise (e.g. infinite scroll
  // loading all products before filtering). The npApplyFilters
  // wrapper in np-collection.js awaits them.
  //
  // afterApply/afterSort/afterClear hooks are synchronous.
  //
  // Used by: np-collection.js, np-collection-mobile.js,
  //          np-infinite-scroll.js
  // ============================================
  window.npFilterHooks = {
    beforeApply: [],
    afterApply: [],
    beforeSort: [],
    afterSort: [],
    afterClear: []
  };

  // ============================================
  // CURRENCY SWITCHING
  // Submits the Shopify localization form. That form POST is the
  // ONLY thing that changes the market server-side — never remove
  // it. A previous v6 did, and currency switching broke entirely
  // (NZD switched straight back to CAD).
  //
  // Called with a null form by np-region-popup's NZD dismiss path,
  // in which case this is currently a no-op.
  //
  // Used by: header-scripts, header-tablet, bag-drawer,
  //          np-mobile-menu, np-region-popup
  // ============================================
  window.npSwitchCurrency = function(countryCode, form) {
    if (form) form.submit();
  };

})();